import {
  BarChart3,
  ClipboardList,
  DollarSign,
  Home,
  Images,
  LogOut,
  MoreHorizontal,
  Settings,
  Scissors,
  UserCog,
  Users,
  WalletCards,
  Workflow,
  X
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/features/auth/AuthProvider";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import { Modal } from "@/shared/components/Modal";
import { OfflineSyncBadge } from "@/shared/components/OfflineSyncBadge";
import { UserDropdownTrigger } from "@/shared/components/UserDropdownTrigger";
import { signOut } from "@/shared/lib/supabase";
import { clearAuthToken } from "@/shared/lib/api";
import { getMobileNavigationPaths, shouldShowMobileMore } from "@/app/mobileNavigation";

const sidebarItems = [
  { to: "/app", label: "Home", icon: Home },
  { to: "/app/clients", label: "Clients", icon: Users },
  { to: "/app/orders", label: "Orders", icon: ClipboardList },
  { to: "/app/payments", label: "Payments", icon: WalletCards },
  { to: "/app/finance", label: "Finance", icon: BarChart3 },
  { to: "/app/styles", label: "Styles", icon: Images },
  { to: "/app/staff", label: "Staff", icon: UserCog },
  { to: "/app/salaries", label: "Salaries", icon: DollarSign },
  { to: "/app/templates", label: "Templates", icon: Workflow },
  { to: "/app/settings", label: "Settings", icon: Settings }
];

const PLAN_BADGE: Record<string, { label: string; className: string }> = {
  beta: { label: "TEAMS Trial", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  free: { label: "PRO", className: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" },
  paid: { label: "PRO", className: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" },
  pro: { label: "PRO", className: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" },
  teams: { label: "TEAMS", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  past_due: { label: "Past Due", className: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
  cancelled: { label: "Cancelled", className: "bg-muted text-muted-foreground" },
};

export function AppLayout() {
  const { authenticated, loading, tailor, authUser, can } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [renderMoreDrawer, setRenderMoreDrawer] = useState(false);
  const moreTouchStartX = useRef<number | null>(null);
  const isStaff = authUser?.role === "staff";
  const isManager = authUser?.role === "manager";
  const staffTaskItem = { to: "/app/tasks", label: "Tasks", icon: ClipboardList };
  const visibleItems = sidebarItems.filter(
    (item) => (!isStaff || !["/app/clients", "/app/orders", "/app/payments", "/app/finance", "/app/styles", "/app/staff", "/app/salaries", "/app/templates"].includes(item.to))
      && (!isManager || !["/app/payments", "/app/finance", "/app/salaries"].includes(item.to))
      && (item.to !== "/app/staff" || can("workers.manage"))
      && (item.to !== "/app/salaries" || can("workers.salaries.manage"))
  );
  const preferredMobileRoutes = ["/app", "/app/orders", "/app/clients", "/app/finance"];
  const primaryMobileItems = [
    ...visibleItems.filter((item) => preferredMobileRoutes.includes(item.to)),
    ...visibleItems.filter((item) => !preferredMobileRoutes.includes(item.to))
  ].slice(0, 4).map((item) => item.to === "/app/payments" ? { ...item, label: "Payment" } : item);
  const primaryMobilePaths = new Set(primaryMobileItems.map((item) => item.to));
  const moreMobileItems = visibleItems.filter((item) => !primaryMobilePaths.has(item.to));
  const navigationItems = isStaff
    ? [visibleItems.find((item) => item.to === "/app") ?? sidebarItems[0], staffTaskItem, visibleItems.find((item) => item.to === "/app/settings") ?? sidebarItems[sidebarItems.length - 1]]
    : visibleItems;
  const isMoreActive = moreMobileItems.some((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));
  const mobileDirectItems = isStaff ? navigationItems : primaryMobileItems;
  const showMoreTab = shouldShowMobileMore(visibleItems.length, isStaff);
  const mobilePaths = getMobileNavigationPaths(isStaff, showMoreTab ? mobileDirectItems.map((item) => item.to) : visibleItems.map((item) => item.to));
  const mobileSourceItems = [...navigationItems, ...visibleItems].filter((item, index, items) => items.findIndex((candidate) => candidate.to === item.to) === index);
  const mobileItems = mobilePaths.map((path) => mobileSourceItems.find((item) => item.to === path)).filter((item): item is typeof sidebarItems[number] => !!item);

  useEffect(() => {
    setShowMore(false);
  }, [location.pathname]);

  useEffect(() => {
    if (showMore) {
      setRenderMoreDrawer(true);
      return;
    }
    const timer = window.setTimeout(() => setRenderMoreDrawer(false), 300);
    return () => window.clearTimeout(timer);
  }, [showMore]);

  useEffect(() => {
    if (location.pathname !== "/app" || !tailor?.id) return;
    try {
      setShowWelcomeModal(window.localStorage.getItem(`tailorbook:welcome:${tailor.id}`) === "pending");
    } catch {
      setShowWelcomeModal(false);
    }
  }, [location.pathname, tailor?.id]);

  const dismissWelcomeModal = () => {
    if (tailor?.id) {
      try {
        window.localStorage.removeItem(`tailorbook:welcome:${tailor.id}`);
      } catch {
        // Continue closing the modal when browser storage is unavailable.
      }
    }
    setShowWelcomeModal(false);
  };

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background px-4">
        <div className="text-center">
          <div className="mx-auto size-10 animate-spin rounded-full border-4 border-muted border-t-primary" />
          <p className="mt-4 text-sm font-semibold">Loading TailorBook</p>
          <p className="mt-1 text-sm text-muted-foreground">Checking your session…</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <div className="min-h-screen bg-background pb-16 md:pb-0">
      <header className="sticky top-0 z-20 border-b border-border bg-surface/95 backdrop-blur supports-[backdrop-filter]:bg-surface/80">
        <div className="mx-auto flex max-w-6xl items-center gap-2 px-3 py-2 sm:gap-3 sm:px-4 sm:py-3">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
              <p className="shrink-0 text-xs font-semibold uppercase tracking-wide text-primary">TailorBook</p>
              {tailor?.subscription_status && (
                <span className={`inline-flex max-w-[88px] items-center truncate rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider sm:max-w-none sm:px-2 sm:text-[10px] ${PLAN_BADGE[tailor.subscription_status]?.className ?? PLAN_BADGE.pro.className}`}>
                  {PLAN_BADGE[tailor.subscription_status]?.label ?? tailor.subscription_status}
                </span>
              )}
            </div>
            <h1 className="truncate text-base font-bold leading-tight sm:text-lg">{tailor?.business_name || "Shop dashboard"}</h1>
          </div>
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <OfflineSyncBadge />
            <div className="relative">
              <UserDropdownTrigger
                fullName={authUser?.full_name || tailor?.full_name || tailor?.business_name || "Account"}
                open={menuOpen}
                onClick={() => setMenuOpen((value) => !value)}
              />
              {menuOpen ? (
                <div className="absolute right-0 top-full z-30 mt-1 w-60 rounded-lg border border-border bg-surface p-1 shadow-lg">
                  <div className="border-b border-border px-3 py-2">
                    <p className="truncate text-sm font-semibold">{tailor?.business_name || "TailorBook"}</p>
                    <p className="truncate text-xs text-muted-foreground">{authUser?.email || tailor?.email || ""}</p>
                  </div>
                  <Link
                    to="/app/settings"
                    className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
                    onClick={() => setMenuOpen(false)}
                  >
                    <Settings size={16} />
                    Settings
                  </Link>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-danger transition hover:bg-red-50"
                    onClick={() => {
                      setMenuOpen(false);
                      setShowSignOutConfirm(true);
                    }}
                  >
                    <LogOut size={16} />
                    Sign out
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-6xl gap-5 px-4 py-5 md:grid-cols-[220px_minmax(0,1fr)]">
        <aside className="hidden md:block">
          <nav className="surface sticky top-20 p-2">
            {navigationItems.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </nav>
        </aside>
        <main className="animate-in min-w-0">
          {((location.pathname.startsWith("/app/staff") && !can("workers.manage")) ||
            (location.pathname.startsWith("/app/salaries") && !can("workers.salaries.manage"))) ? (
            <div className="surface p-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary">TEAMS plan feature</p>
              <h2 className="mt-2 text-xl font-bold">Team management is not available on PRO</h2>
              <p className="mt-2 max-w-xl text-sm text-muted-foreground">Upgrade to TEAMS to manage workers, assignments, commissions, and salaries. Your existing historical records remain preserved.</p>
              <Link className="btn-primary mt-5 inline-flex" to="/app/settings">View subscription</Link>
            </div>
          ) : <Outlet />}
        </main>
      </div>

      <nav className={`fixed inset-x-0 bottom-0 z-30 border-t border-border bg-surface/95 shadow-[0_-6px_20px_hsl(var(--foreground)/.06)] backdrop-blur safe-bottom md:hidden ${isStaff ? "grid grid-cols-3" : "flex"}`}>
        {mobileItems.map((item) => (
          <NavItem key={item.to} {...item} compact />
        ))}
        {showMoreTab ? <button
          type="button"
          className={`flex min-h-14 min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 py-1.5 text-[10px] font-medium transition ${isMoreActive || showMore ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
          aria-expanded={showMore}
          aria-controls="mobile-more-sheet"
          onClick={() => setShowMore(true)}
        >
          <MoreHorizontal aria-hidden size={18} />
          <span>More</span>
        </button> : null}
      </nav>

      {renderMoreDrawer ? (
        <>
          <button
            type="button"
            aria-label="Close more navigation"
            className={`fixed inset-0 z-40 bg-foreground/35 transition-opacity duration-300 md:hidden ${showMore ? "opacity-100" : "pointer-events-none opacity-0"}`}
            onClick={() => setShowMore(false)}
          />
          <section
            id="mobile-more-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-more-title"
            aria-hidden={!showMore}
            className={`fixed inset-y-0 right-0 z-50 w-[80vw] max-w-[80vw] border-l border-border bg-surface px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] pt-4 shadow-2xl transition-transform duration-300 ease-in-out md:hidden ${showMore ? "translate-x-0" : "translate-x-full"}`}
            onTouchStart={(event) => { moreTouchStartX.current = event.touches[0]?.clientX ?? null; }}
            onTouchEnd={(event) => {
              const startX = moreTouchStartX.current;
              const endY = event.changedTouches[0]?.clientX;
              moreTouchStartX.current = null;
              if (startX !== null && endY !== undefined && endY - startX > 60) setShowMore(false);
            }}
          >
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-primary">Workspace</p>
                <h2 id="mobile-more-title" className="mt-1 text-lg font-bold">More tools</h2>
              </div>
              <button type="button" className="flex size-10 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Close more navigation" onClick={() => setShowMore(false)}>
                <X aria-hidden size={20} />
              </button>
            </div>
            <nav className="flex flex-col gap-1 pt-3">
              {moreMobileItems.map((item) => (
                <NavItem key={item.to} {...item} onClick={() => setShowMore(false)} />
              ))}
            </nav>
          </section>
        </>
      ) : null}

      <ConfirmDialog
        open={showSignOutConfirm}
        title="Sign out?"
        message="You will need to sign in again to access your TailorBook account."
        confirmLabel="Sign out"
        cancelLabel="Cancel"
        busy={signingOut}
        onConfirm={() => {
          setShowSignOutConfirm(false);
          setSigningOut(true);
          signOut()
            .then(() => {
              clearAuthToken();
              window.location.href = "/auth";
            })
            .catch(() => {
              toast.error("Could not sign out");
              setSigningOut(false);
            });
        }}
        onCancel={() => setShowSignOutConfirm(false)}
      />

      <Modal
        open={showWelcomeModal}
        onClose={dismissWelcomeModal}
        ariaLabel="Welcome to TailorBook"
        size="md"
      >
        <div className="space-y-5 pt-2 text-center">
          <div className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-sm">
            <Scissors aria-hidden size={28} />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">TailorBook</p>
            <h2 className="mt-2 text-2xl font-bold">
              Welcome to TailorBook, {(tailor?.full_name || tailor?.business_name || "there").trim().split(/\s+/)[0]}!
            </h2>
            <p className="mt-2 text-base font-semibold text-primary">You&apos;re on a 30-Day TEAMS Trial</p>
          </div>
          <div className="space-y-4 text-left text-sm leading-6 text-muted-foreground">
            <p>
              We&apos;re currently rolling out early access to refine the platform. Enjoy 30 days of full TEAMS access at no cost, including clients, orders, finance, staff, assignments, commissions, salaries, and collaboration tools.
            </p>
            <div className="rounded-xl border border-border bg-muted/40 p-4">
              <p className="font-semibold text-foreground">What happens after 30 days?</p>
              <p className="mt-1">
                Choose between our <span className="font-semibold text-foreground">PRO (Solo Tailors)</span> or <span className="font-semibold text-foreground">TEAMS</span> plans to keep your workspace running. No credit card required today.
              </p>
            </div>
          </div>
          <button type="button" className="btn-primary w-full justify-center" onClick={dismissWelcomeModal}>
            Explore Workspace
          </button>
          <p className="text-xs leading-5 text-muted-foreground">
            Got feedback? Let us know anytime via{" "}
            <Link className="font-semibold text-primary hover:underline" to="/app/settings/support/new" onClick={dismissWelcomeModal}>
              in-app support chat
            </Link>
            .
          </p>
        </div>
      </Modal>
    </div>
  );
}

function NavItem({
  to,
  label,
  icon: Icon,
  compact = false,
  onClick
}: {
  to: string;
  label: string;
  icon: typeof Home;
  compact?: boolean;
  onClick?: () => void;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          "flex items-center gap-3 rounded-md text-sm font-medium transition shrink-0",
          compact ? "min-h-14 min-w-0 flex-1 flex-col justify-center gap-1 px-1 py-1.5" : "min-h-12 w-full px-3 py-2",
          isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
        ].join(" ")
      }
      end={to === "/app"}
      onClick={onClick}
    >
      <Icon aria-hidden size={compact ? 18 : 18} />
      <span className={compact ? "max-w-full truncate text-[10px] whitespace-nowrap" : undefined}>{label}</span>
    </NavLink>
  );
}
